const post = {
  slug: "sell-clothes-on-instagram-india",
  title: "How to Sell Clothes on Instagram in India: A Practical Guide",
  seoTitle: "How to Sell Clothes on Instagram in India",
  description:
    "A practical guide to selling clothes on Instagram in India: photos, size charts, stock per size, payments, returns and shipping, plus mistakes that cause returns.",
  excerpt:
    "Clothing is one of the easiest things to sell on Instagram and one of the hardest to get right, because of sizes and returns. Here is how to set up, photograph, price and ship.",
  category: "Selling on Instagram",
  tags: ["Sell clothes on Instagram", "Instagram clothing business India", "Fashion seller guide"],
  date: "2026-11-04",
  cover: {
    src: "/blog-images/sell-clothes-on-instagram-india-cover.png",
    alt: "A phone showing an Instagram DM where a customer chooses a clothing size and pays inside the chat",
  },
  blocks: [
    {
      type: "p",
      text:
        "Clothing sells well on Instagram because it is visual. A good photo or Reel can do more than a long description. But clothing is also tricky: sizes, colours, fit and returns create more questions and more problems than most products.",
    },
    {
      type: "p",
      text:
        "This guide covers what a small clothing seller in India needs: how to show your products, how to handle sizes and stock, how to get paid, and how to deal with returns and shipping. You can follow it whether you make your own clothes or resell.",
    },

    { type: "h2", text: "Start with a small, clear range" },
    {
      type: "p",
      text:
        "It is tempting to list everything. Resist that. A small range of items you can photograph well, stock reliably and describe accurately will sell better than a messy catalog. Start with a handful of products, learn what customers ask, and add more as you go.",
    },

    { type: "h2", text: "Show clothes properly" },
    {
      type: "ul",
      items: [
        "**Use natural light.** It shows true colour, which cuts down on \u201Cit looked different\u201D returns.",
        "**Show the front, back and a close-up.** Fabric texture and details help people trust what they are buying.",
        "**Show it worn, and flat.** A worn photo shows how it looks, and a flat photo shows the shape and length.",
        "**Mention the model\u2019s height and size.** It gives customers something to compare with.",
        "**Keep a consistent style.** A recognisable look across posts makes your shop feel established.",
      ],
    },
    {
      type: "p",
      text:
        "Our guide to [Instagram catalog photos and sizes](/blog/instagram-catalog-tips-photos-sizes) goes into more detail, and [writing product descriptions](/blog/write-product-descriptions-instagram-sellers) covers the words that go with them.",
    },

    { type: "h2", text: "Get sizes right" },
    {
      type: "p",
      text:
        "Wrong-size orders are the most common reason clothing comes back. Prevent them before they happen.",
    },
    {
      type: "ol",
      items: [
        "**Publish a size chart.** Give real measurements in inches or centimetres, such as chest, waist and length, and not only S, M and L.",
        "**Say how it fits.** Note if it runs small, large or true to size.",
        "**Offer help choosing.** A simple question like \u201CWhat is your height and usual size?\u201D often prevents a wrong order.",
        "**Track stock per size.** Do not offer a size you do not have. Selling a sold-out size leads to delays, apologies and cancellations.",
      ],
    },
    {
      type: "p",
      text:
        "In Checkout DM, stock is tracked for each size, and sold-out sizes are not offered to the buyer, so the customer only sees what you can ship.",
    },

    { type: "h2", text: "Price it properly" },
    {
      type: "p",
      text:
        "Clothing has costs beyond the fabric or the wholesale price: packaging, shipping, payment fees and returns. Add them all up before you set a price, or you may sell a lot and earn very little. Our guide to [pricing products on Instagram](/blog/price-products-on-instagram-india) has a worked example in rupees, with a returns allowance built in.",
    },

    { type: "h2", text: "Make ordering easy" },
    {
      type: "p",
      text:
        "Customers who like a piece want to buy it quickly. Every extra step loses some of them. The easiest path is a chat where they pick the product, choose a size, enter their address and pay, all in one place. See [selling on Instagram without losing orders](/blog/sell-on-instagram-dms-without-losing-orders) for how that flow works. No website yet? Read how to [sell on Instagram without a website](/blog/sell-on-instagram-without-a-website).",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "Choosing a size",
      caption: "Illustrative example. Use your own products and wording.",
      messages: [
        { from: "buyer", text: "Is the kurta available in L?" },
        { from: "shop", text: "Sorry, L is sold out right now. M and XL are in stock. Chest for M is 38 inches and for XL is 44 inches. Which would suit you?" },
        { from: "buyer", text: "XL" },
        { from: "shop", text: "XL is in stock. Shall I send the payment link?" },
      ],
    },

    { type: "h2", text: "Decide how customers will pay" },
    {
      type: "p",
      text:
        "Most Indian customers are comfortable with UPI, and many sellers start there. The risk is fake screenshots and manual checking. A payment gateway confirms payments automatically. Our guides to [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) and [cash on delivery versus prepaid orders](/blog/cash-on-delivery-vs-prepaid-instagram-sellers) help you choose, and prepaid orders also lower the chance that a parcel comes back.",
    },

    { type: "h2", text: "Have a clear return and exchange rule" },
    {
      type: "p",
      text:
        "Clothing returns are normal. A clear rule protects you and reassures buyers. Many sellers offer exchanges for size problems instead of refunds, since the customer still gets something they want and you keep the sale. Write down the number of days, the condition the item must be in and who pays for return shipping. Our guide to [writing a return policy](/blog/return-policy-for-instagram-shop) includes a sample.",
    },

    { type: "h2", text: "Pack and ship carefully" },
    {
      type: "p",
      text:
        "Clothes are easier to ship than fragile goods, but they still need care. Use a sturdy mailer, put items in a plastic pouch against water and add a thank-you note. Share the tracking link as soon as the order ships, and confirm the delivery address before you send. See [packing and shipping Instagram orders](/blog/pack-and-ship-instagram-orders) and [reducing RTO](/blog/reduce-rto-instagram-sellers-india) for more.",
    },

    { type: "h2", text: "Mistakes that cause returns" },
    {
      type: "ul",
      items: [
        "**Photos with heavy filters or odd lighting,** so the colour is not what arrives.",
        "**No size chart,** so customers guess.",
        "**Selling sizes you do not have,** which leads to substitutions.",
        "**Promising delivery dates you cannot keep.**",
        "**No clear return rule,** which turns every return into an argument.",
      ],
    },
    {
      type: "callout",
      tone: "lilac",
      title: "Start small and learn",
      text:
        "Launch with a few products, handle your first orders carefully and note every question customers ask. Those questions tell you what to add to your descriptions, size charts and replies.",
    },
  ],
  related: [
    { to: "/catalog-guide", title: "Catalog Guide", description: "Product cards, stock, sizes and in-DM search." },
    { to: "/pricing", title: "Pricing", description: "Compare plans, from the Free plan to Pro." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "How do I start a clothing business on Instagram in India?",
      a: "Start with a small range of items you can photograph well and keep in stock, publish a size chart, decide how customers will pay, write a clear return or exchange rule and make ordering easy inside your DMs.",
    },
    {
      q: "How do I reduce returns when selling clothes online?",
      a: "Use accurate photos in natural light, give real measurements and a size chart, say how the item fits, track stock per size and offer help choosing a size before the order.",
    },
    {
      q: "Do I need a website to sell clothes on Instagram?",
      a: "No. You can take orders and payments through DMs, and a shareable storefront link can serve customers who are not on Instagram. A website becomes useful if you want to rank on Google or sell across several channels.",
    },
    {
      q: "Can Checkout DM track stock for each size?",
      a: "Yes. Checkout DM tracks stock per size, and sold-out sizes are not offered to the buyer.",
    },
  ],
};

export default post;
